import { memo, useCallback } from "react";
import { Spinner, InputGroup } from "react-bootstrap";
import styled from "styled-components";
import { DebounceInput } from "react-debounce-input";
import { useTranslation } from "react-i18next";

const Wrapper = styled.div<{ $fullWidth?: boolean }>`
  width: 100%;
  max-width: ${(props) => (props.$fullWidth ? "none" : "500px")};
`;

const StyledInputGroup = styled(InputGroup)`
  border: solid 1px #e8e8ef;
  border-radius: 10px;
  background: white;
  overflow: hidden;

  .input-group-text {
    background: white;
    border: none;
    color: #21b400;
    padding-left: 15px;
  }

  input {
    border: none !important;
    box-shadow: none !important;
    padding: 12px 15px;
    font-size: 16px;

    &:focus {
      box-shadow: none !important;
      border: none !important;
    }
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-right: 15px;

  .spinner-border {
    color: #21b400 !important;
  }
`;

interface DomainSearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  loading?: boolean;
  placeholder?: string;
  debounceTimeout?: number;
  fullWidth?: boolean;
}

export function isValidDomain(domain: string): boolean {
  const trimmed = domain.trim();
  // Allow single word (letters/numbers only, 1-63 chars)
  if (/^[A-Za-z0-9]{1,63}$/.test(trimmed)) return true;
  // Or allow valid domain (must have at least one dot, valid chars, no leading/trailing dot/dash)
  return /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z]{2,})+$/.test(trimmed);
}

const DomainSearchBox = ({
  value,
  onChange,
  loading = false,
  placeholder,
  debounceTimeout = 500,
  fullWidth = false,
}: DomainSearchBoxProps) => {
  const { t } = useTranslation("common");
  const resolvedPlaceholder = placeholder ?? t("searchPlaceholder");
  const handleInputChange = useCallback(
    (e: any) => {
      const newValue = e.target.value;
      onChange(newValue);
    },
    [onChange],
  );

  return (
    <Wrapper $fullWidth={fullWidth}>
      <StyledInputGroup>
        <InputGroup.Text>
          <i className="bi bi-search"></i>
        </InputGroup.Text>
        <DebounceInput
          element="input"
          className="form-control"
          type="text"
          placeholder={resolvedPlaceholder}
          minLength={2}
          debounceTimeout={debounceTimeout}
          onChange={handleInputChange}
          spellCheck={false}
          value={value}
        />
        {loading && (
          <SpinnerWrapper>
            <Spinner
              as="span"
              animation="border"
              role="status"
              size="sm"
              aria-hidden="true"
            />
          </SpinnerWrapper>
        )}
      </StyledInputGroup>
    </Wrapper>
  );
};

export default memo(DomainSearchBox);
